import { useState } from 'react';
import { useShowToast } from '@/hooks';
import { getAtsJobCreate } from '@/store/atsJobCreate';
import { atsJobDetailsApiActions } from '@/store/atsJobDetails';
import { getRejectionEmailFormData } from '@/utils/getEmailFormConfig';
import {
  Box,
  Button,
  Flex,
  Icon,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@heroicons/react/solid';
import { MinusCircle } from '@kula-ai/icons/outline/MinusCircle';
import { Lightning01 } from '@kula-ai/icons/dual-tone/Lightning01';
import { Menu, type MenuConfig } from '@kula-ai/components';
import { CheckCircle } from '@kula-ai/icons-core';
import { useDispatch, useSelector } from 'react-redux';
import { RejectModal } from '@/components/ats/reject-modal/RejectModal';
import { TruncatedTextWithTooltip } from '@/ui/TooltipText';
import { useRejectApplicationMutation } from '@/store/query/ats-applications';
import { selectUsers } from '@/store/users/selectors';
import { selectSettings, selectCurrUser } from '@/store/bootstrap/selectors';
import AddToHRISModal from '@/components/shared/hris/AddToHRISModal';
import { useGetAllUsers } from '@/hooks/useGetAllUsers';
import { useKFlags } from '@/hooks/use-k-flags';
import { AssociateReqModal } from '@/components/shared/requisitions/associate-req/AssociateReqModal';
import { RemoveRequisitionConfirmModal } from '@/components/shared/RemoveRequisitionConfirmModal';
import { isHiredMilestoneStage } from '@/ui-library/components/constants';
import { useRejection } from '@/hooks/useRejection';
import {
  CONSENT_BLOCKED_TOOLTIP,
  isConsentBlocked,
} from '@/components/consent';
import {
  useMoveStageWithAutomationConfirm,
  deriveCurrentStageStopping,
  MOVE_WITH_AUTOMATION_STRINGS,
  deriveStageAutomation,
  getActivityLabel,
  getCandidateDisplayName,
  getCandidateAvatarUrl,
} from '@/components/automation/MoveWithAutomationModal';
import { firstStepAlreadyRan } from '@/components/automation/UnderwayPopup';
// import { CreateProjectDrawer } from '@/pages/projects/components/project-modal/create-project-drawer';

export const MoveToStage = ({
  applicationData,
  refetch = () => {},
  textProps = {},
  text = 'Move to stage',
  buttonProps = {},
  showRejectAsButton = false,
  triggerReference,
  isRejected = false,
}) => {
  const { validateRejectionEmailPayload } = useRejection();
  const { getInterviewPlan, currentJob } = useSelector(getAtsJobCreate);
  const { data: stages } = getInterviewPlan || {};
  const stagesList = applicationData?.ats_job_stages || stages;
  const stagesToShow = stagesList?.filter((stage) => !stage?.deleted);
  const [actionLoading, setActionLoading] = useState(false);
  const [pendingMoveStageId, setPendingMoveStageId] = useState(null);
  const [pendingRemoveReqStageId, setPendingRemoveReqStageId] = useState(null);
  const rejectModalActions = useDisclosure();
  const integrationsModalAction = useDisclosure();
  const associateReqModalActions = useDisclosure();
  const removeReqConfirmModalActions = useDisclosure();
  const toast = useShowToast();
  const dispatch = useDispatch();
  const [rejectApplication] = useRejectApplicationMutation();
  // Gates moves into stages with an active workflow_rule — preflight
  // hits the same cache key the interview-plan drawer uses, so it's
  // free if the rule was already loaded.
  const {
    confirmAndMove: confirmAndMoveStage,
    modalElement: moveAutomationModalElement,
  } = useMoveStageWithAutomationConfirm();
  const { processFromUserPayload, feSharedEmails } = useGetAllUsers();
  const { feReq, feSurvey, feStageAutomation } = useKFlags();

  // Stages with an active rule come straight from the interview-plan stages
  // (each carries a per-stage `automation` field) — no separate
  // workflow_rules fetch. We read from `stages` (getInterviewPlan) rather
  // than `applicationData.ats_job_stages`, since the application show payload
  // doesn't include per-stage automation.
  const { stageIds: stagesWithAutomation } = deriveStageAutomation(
    feStageAutomation ? stages : []
  );

  const users = useSelector(selectUsers);
  const settings = useSelector(selectSettings);
  const currUser = useSelector(selectCurrUser);
  const jobHasRequisitions =
    currentJob?.data?.basic_info?.requisitions[0]?.role_name;
  const isRequisitionsEnabled =
    (feReq &&
      currUser?.requisition?.enabled &&
      (jobHasRequisitions || currentJob?.data?.switched_to_new_req)) ??
    false;
  const { ats_job } = applicationData || {};
  const { job_status } = ats_job || {};

  const isJobUpdateAllowedStatus = !['closed', 'archived']?.includes(
    job_status
  );

  //TODO: Implement Create-Project Drawer
  // const createProjectActions = useDisclosure();
  const clickHandler = (stageId) => {
    // "Hired" is a MILESTONE — a job can have several custom-named hired stages,
    // so match every stage carrying it, not just the first.
    const hiredMilestoneStageIds = stages?.flatMap((i) =>
      isHiredMilestoneStage(i) ? [i.id] : []
    );

    const isMovingFromHiredStage = Boolean(
      hiredMilestoneStageIds?.includes(applicationData?.job_stage_id)
    );
    const hasRequisition = applicationData?.requisition?.id;
    const hasAtsOffer = applicationData?.ats_offer?.id;

    if (
      isMovingFromHiredStage &&
      isRequisitionsEnabled &&
      hasRequisition &&
      !hasAtsOffer &&
      !hiredMilestoneStageIds?.includes(stageId)
    ) {
      // Show confirmation modal to remove requisition
      setPendingRemoveReqStageId(stageId);
      removeReqConfirmModalActions?.onOpen();
      return;
    }

    handleClick(stageId);
  };

  const dispatchMoveToStage = async (
    stageId,
    requisitionCodes,
    skipAutomation: boolean,
    isHiredStage: boolean
  ) => {
    const allIntegrations =
      settings?.account?.account_integrations?.hris_integrations || [];
    const pushed = applicationData?.pushed_to_hris || [];
    const unpushed = allIntegrations.filter(
      (i) => i.status === 'active' && !pushed.includes(i.type)
    );

    try {
      setActionLoading(true);

      // Build request config with optional requisition_codes + automation skip.
      const requestConfig = {
        to_stage_id: stageId,
        ...(requisitionCodes && { requisition_code: requisitionCodes }),
        ...(skipAutomation && { skip_automation: true }),
      };

      // Make API request to move to new stage
      const response: any = await dispatch(
        atsJobDetailsApiActions('moveToStage', {
          pathParams: { applicationId: applicationData?.id },
          requestConfig,
          isApi: true,
        })
      );

      // Handle response
      if (response?.errors?.length) {
        toast({ title: 'Moving to new stage failed', status: 'error' });
        return { pendingExecutionIds: [] };
      } else {
        toast({ title: 'Candidate stage updated' });

        const shouldOpenHRISModal =
          unpushed.length > 0 &&
          (isHiredStage || response?.data?.status === 'hired');

        if (shouldOpenHRISModal) {
          const originalOnClose = integrationsModalAction?.onClose;
          integrationsModalAction.onClose = () => {
            if (triggerReference === 'table') refetch();
            originalOnClose?.();
          };
          integrationsModalAction?.onOpen();
          if (triggerReference === 'application') {
            refetch();
          }
        } else {
          refetch();
        }

        const pendingId = response?.data?.pending_workflow_execution_id;
        return {
          pendingExecutionIds: pendingId ? [pendingId] : [],
          firstStepAlreadyRan: firstStepAlreadyRan(response?.data?.next_action),
          nextAction: response?.data?.next_action,
        };
      }
    } catch (error) {
      console.error('MoveToStage.jsx:handleClick', error);
    } finally {
      setActionLoading(false);
    }
    return { pendingExecutionIds: [] };
  };

  const handleClick = async (stageId, requisitionCodes = null) => {
    // Skip if stage is already selected
    if (stageId === applicationData?.job_stage_id) return;

    // True when the TARGET stage carries the Hired milestone — covers any
    // custom-named hired stage, and multiple of them.
    const isHiredStage = Boolean(
      applicationData?.ats_job_stages?.some(
        (stage) => isHiredMilestoneStage(stage) && stage?.id === stageId
      )
    );

    const hasRequisition = applicationData?.requisition?.id;

    // Check if we need to show requisition modal
    if (
      isHiredStage &&
      isRequisitionsEnabled &&
      !hasRequisition &&
      !requisitionCodes
    ) {
      setPendingMoveStageId(stageId);
      associateReqModalActions?.onOpen();
      return;
    }

    // Feature-flag gate: when `feStageAutomation` is off, bypass the
    // confirm modal entirely and dispatch the plain pre-automation
    // move. The confirm hook + its preflight fetch are still
    // initialised but never invoked.
    if (!feStageAutomation) {
      await dispatchMoveToStage(stageId, requisitionCodes, false, isHiredStage);
      return;
    }

    // Gate the actual dispatch through the automation-confirm modal.
    // When the target stage has no workflow_rule, this falls through
    // to `dispatchMoveToStage(false)` immediately (no modal).
    const targetStage = stagesList?.find(
      (s) => String(s?.id) === String(stageId)
    );
    const { currentStageName, currentStageStoppingActions } =
      deriveCurrentStageStopping(applicationData);
    await confirmAndMoveStage({
      targetStageId: stageId,
      targetStageName:
        targetStage?.name ?? MOVE_WITH_AUTOMATION_STRINGS.fallback.stageName,
      currentStageName,
      currentStageStoppingActions,
      applicationId: applicationData?.id,
      // Moves into the Hired stage skip the automation-confirm modal and enqueue
      // the underway popup item directly — same as the quick-app-review loop
      // (and covers the associate-requisition sub-flow, which is always a Hired
      // move). The hook still runs its immediate-first-step + rule preflight, so
      // a Hired stage without automation just falls through to a plain move.
      autoConfirm: isHiredStage,
      performMove: (skipAutomation) =>
        dispatchMoveToStage(
          stageId,
          requisitionCodes,
          skipAutomation,
          isHiredStage
        ),
      // Without this block the confirm modal opens and the move
      // succeeds, but the hook has nothing to enqueue into the
      // underway popup. Mirrors the shape kanban / contacts / QAR
      // already pass — single-candidate, derived from the moving
      // application + the target stage.
      underway: {
        getEnqueueInputBase: (rule) => ({
          activityKind: rule.actions?.[0]?.target?.kind ?? 'email',
          activityLabel: getActivityLabel(rule),
          stageId,
          stageName:
            targetStage?.name ??
            MOVE_WITH_AUTOMATION_STRINGS.fallback.stageName,
          jobId: applicationData?.ats_job?.id ?? applicationData?.job_id ?? '',
          jobName: applicationData?.ats_job?.name ?? '',
          candidates: [
            {
              id: String(applicationData?.id),
              name: getCandidateDisplayName(applicationData),
              avatarUrl: getCandidateAvatarUrl(applicationData),
            },
          ],
          isBulk: false,
        }),
      },
    });
  };

  const handleAssociateRequisitions = async (requisitions) => {
    associateReqModalActions?.onClose();

    // Extract requisition codes
    const requisitionCodes = Array.isArray(requisitions)
      ? requisitions.map((req) => req.code).filter(Boolean)
      : requisitions?.code
        ? [requisitions.code]
        : [];

    if (requisitionCodes.length > 0) {
      const reqCount = requisitionCodes.length;
      toast({
        title: `Requisition${reqCount > 1 ? 's' : ''} associated successfully`,
        status: 'success',
      });
    }

    if (pendingMoveStageId) {
      await handleClick(pendingMoveStageId, requisitionCodes);
      setPendingMoveStageId(null);
    }
  };

  const handleRemoveRequisitionConfirm = async () => {
    removeReqConfirmModalActions?.onClose();

    if (pendingRemoveReqStageId) {
      await handleClick(pendingRemoveReqStageId);
      setPendingRemoveReqStageId(null);
    }
  };

  const handleReject = async (values) => {
    try {
      setActionLoading(true);
      const { sendRejectEmail } = values;

      // Since the validation is common across all rejection scenarios, kept the logic useRejection hook here.
      if (!validateRejectionEmailPayload(sendRejectEmail, values || {})) {
        setActionLoading(false);
        return;
      }

      const selectedProjectId = values.addToProject
        ? values.selectedProject
        : '';

      const payload = getRejectionEmailFormData({
        values,
        sendRejectEmail,
        feSurvey,
        processFromUserPayload,
        feSharedEmails,
        users,
      });

      if (selectedProjectId) {
        payload.append('project_id', selectedProjectId);
      }
      const response = await rejectApplication({
        applicationId: applicationData?.id,
        payload,
      });

      if ((response as any)?.error?.length) {
        toast({
          title: 'Rejecting the candidate failed',
          status: 'error',
          description: (response as any)?.error?.[0],
        });
      } else {
        toast({ title: 'Candidate rejected successfully' });
        rejectModalActions?.onClose();
        refetch();
      }
    } catch (err) {
      console.error('MoveToStage.jsx:handleReject', err);
    } finally {
      setActionLoading(false);
    }
  };

  // Build the kula Menu config from the stages list + optional Reject
  // row. Selected-stage tick uses CheckCircle (size 18) to match kula
  // Select's selected-option indicator — kula Menu's built-in
  // `checked` prop renders a plain Check (size 13), which doesn't
  // match. The Lightning marker is embedded inside the `label` so it
  // stays next to the stage name even when the tick is rendered.
  const stageItems: MenuConfig[] = (stagesToShow ?? []).map((stage: any) => {
    const isSelected = stage?.id === applicationData?.job_stage_id;
    const hasAutomation = stagesWithAutomation.has(String(stage?.id));
    return {
      key: stage?.id,
      // Selected row takes kula Select's selected-option background so it reads
      // the same as the select dropdown (secondary-25).
      className: isSelected ? 'bg-secondary-25' : undefined,
      label: (
        <Flex alignItems="center" gap="6px" minW={0} w="100%">
          <Box flex="1" minW={0}>
            <TruncatedTextWithTooltip
              color={isSelected && 'ats-primary'}
              fontSize="sm"
              fontWeight={isSelected && 600}
              text={stage?.name}
              enableTooltip={true}
              maxWidth="350px"
            />
          </Box>
          {hasAutomation && (
            <Flex
              alignItems="center"
              justifyContent="center"
              bg="pastel-3-light"
              borderRadius="6px"
              px="2px"
              py="4px"
              flexShrink={0}
              ml="auto"
            >
              <Icon as={Lightning01} w="14px" h="14px" color="pastel-3-dark" />
            </Flex>
          )}
        </Flex>
      ),
      rightIcon: isSelected ? (
        // flex + center (not inline-flex) so the tick sits dead-centre in its
        // slot — inline-flex picks up the text baseline and drifts slightly.
        <Box
          as="span"
          color="ats-primary"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <CheckCircle size={18} />
        </Box>
      ) : undefined,
      onClick: () => clickHandler(stage?.id),
    };
  });
  const menuItems: MenuConfig[] = showRejectAsButton
    ? stageItems
    : [
        ...stageItems,
        { type: 'separator', key: 'reject-sep' },
        {
          key: 'reject',
          label: 'Reject candidate',
          icon: <Icon as={MinusCircle} />,
          destructive: true,
          onClick: () => rejectModalActions?.onOpen(),
        },
      ];

  // A declined/withdrawn consent bars a stage move — reject stays available.
  const consentBlocked = isConsentBlocked(applicationData?.candidate_consent);

  const moveTrigger = (
    <Button
      height="32px"
      variant="primary"
      rightIcon={<Icon as={ChevronDownIcon} />}
      data-test-id="move-to-stage-button"
      isDisabled={consentBlocked}
      {...buttonProps}
    >
      <TruncatedTextWithTooltip
        color="inherit"
        {...textProps}
        text={text}
        enableTooltip={true}
      />
    </Button>
  );

  return (
    <>
      <Flex gap="6px">
        {!isRejected && isJobUpdateAllowedStatus && (
          <Menu
            align="start"
            trigger={
              consentBlocked ? (
                // Chakra swallows events on a disabled button — the span carries the tooltip.
                <Tooltip
                  label={CONSENT_BLOCKED_TOOLTIP}
                  hasArrow
                  placement="bottom"
                >
                  <Box as="span" display="inline-flex">
                    {moveTrigger}
                  </Box>
                </Tooltip>
              ) : (
                moveTrigger
              )
            }
            items={menuItems}
          />
        )}
        {showRejectAsButton && !isRejected && (
          <Button
            variant="secondary"
            bg="error-200"
            color="base-white"
            maxH="34px"
            _hover={{
              bg: 'error-100',
            }}
            _active={{}}
            onClick={() => rejectModalActions?.onOpen()}
            border="none"
            data-test-id="candidate-detail-reject-button"
          >
            Reject
          </Button>
        )}
      </Flex>
      {integrationsModalAction?.isOpen && (
        <AddToHRISModal
          modalActions={integrationsModalAction}
          applicationData={applicationData}
          refetch={refetch}
        />
      )}
      {rejectModalActions?.isOpen && (
        <RejectModal
          rejectModalActions={rejectModalActions}
          handleSubmit={handleReject}
          isLoading={actionLoading}
          actionApplication={applicationData}
          isProjectCreationAllowed={false}
          // onProjectClick={() => createProjectActions?.onOpen()}
        />
      )}
      {associateReqModalActions?.isOpen && (
        <AssociateReqModal
          isOpen={associateReqModalActions?.isOpen}
          onClose={() => {
            associateReqModalActions?.onClose();
            setPendingMoveStageId(null);
          }}
          onAssociate={handleAssociateRequisitions}
          multiSelect={false}
        />
      )}
      {removeReqConfirmModalActions?.isOpen && (
        <RemoveRequisitionConfirmModal
          isOpen={removeReqConfirmModalActions?.isOpen}
          onClose={() => {
            removeReqConfirmModalActions?.onClose();
            setPendingRemoveReqStageId(null);
          }}
          onConfirm={handleRemoveRequisitionConfirm}
          isLoading={actionLoading}
          applicationData={applicationData}
        />
      )}
      {/* {createProjectActions?.isOpen && (
        <CreateProjectDrawer
          modalActions={createProjectActions}
          setCreatedProjectData={() => {}}
          mode="create"
        />
      )} */}
      {feStageAutomation && moveAutomationModalElement}
    </>
  );
};
